import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, TrendingUp, Calculator, Home, BadgeDollarSign } from "lucide-react";
import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export const Route = createFileRoute("/calculadora")({
  head: () => ({
    meta: [
      { title: "Calculadora — Bizaclia" },
      {
        name: "description",
        content:
          "Calcula la rentabilidad de tu inversión, valora un traspaso o simula tu hipoteca para locales comerciales.",
      },
      { property: "og:title", content: "Calculadora — Bizaclia" },
      {
        property: "og:description",
        content:
          "Herramientas de cálculo para inversores, arrendadores y traspasadores de locales comerciales.",
      },
      { property: "og:url", content: "/calculadora" },
    ],
    links: [{ rel: "canonical", href: "/calculadora" }],
  }),
  component: CalculadoraPage,
});

function CalculadoraPage() {
  return (
    <main className="mx-auto max-w-5xl px-4 py-10">
      <Link
        to="/"
        className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Volver al inicio
      </Link>

      <header className="mb-10">
        <h1 className="text-4xl font-bold tracking-tight">Calculadora</h1>
        <p className="mt-2 text-muted-foreground max-w-2xl">
          Herramientas de cálculo para tomar mejores decisiones en el mercado inmobiliario comercial.
        </p>
      </header>

      <Tabs defaultValue="rentabilidad" className="w-full">
        <TabsList className="grid w-full grid-cols-3 h-auto gap-2 p-2">
          <TabsTrigger value="rentabilidad" className="gap-2 py-3 text-sm">
            <TrendingUp className="h-4 w-4" /> Rentabilidad
          </TabsTrigger>
          <TabsTrigger value="traspaso" className="gap-2 py-3 text-sm">
            <BadgeDollarSign className="h-4 w-4" /> Valoración traspaso
          </TabsTrigger>
          <TabsTrigger value="hipoteca" className="gap-2 py-3 text-sm">
            <Home className="h-4 w-4" /> Hipoteca
          </TabsTrigger>
        </TabsList>

        <TabsContent value="rentabilidad" className="mt-6">
          <RentabilidadCalc />
        </TabsContent>
        <TabsContent value="traspaso" className="mt-6">
          <TraspasoCalc />
        </TabsContent>
        <TabsContent value="hipoteca" className="mt-6">
          <HipotecaCalc />
        </TabsContent>
      </Tabs>

      <section className="mt-14 rounded-xl border border-border bg-background p-6 text-center">
        <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 mb-3">
          <Calculator className="h-5 w-5 text-primary" />
        </div>
        <h2 className="text-xl font-semibold">¿Necesitas ayuda con los números?</h2>
        <p className="mt-2 text-sm text-muted-foreground max-w-lg mx-auto">
          Nuestro equipo puede asesorarte en la valoración de tu local, negocio o inversión.
        </p>
        <div className="mt-5 flex flex-wrap justify-center gap-3">
          <Link
            to="/contacto"
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Contactar
          </Link>
          <Link
            to="/mybizaclia"
            className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            Ir a MyBizaclia
          </Link>
        </div>
      </section>
    </main>
  );
}

/* ─── Rentabilidad de inversión ─── */
function RentabilidadCalc() {
  const [precio, setPrecio] = useState<string>("150000");
  const [rentaMensual, setRentaMensual] = useState<string>("1200");
  const [gastosMensual, setGastosMensual] = useState<string>("200");
  const [impuestosCompra, setImpuestosCompra] = useState<string>("10");

  const precioNum = parseFloat(precio) || 0;
  const rentaNum = parseFloat(rentaMensual) || 0;
  const gastosNum = parseFloat(gastosMensual) || 0;
  const impuestosPct = parseFloat(impuestosCompra) || 0;

  const inversionTotal = precioNum * (1 + impuestosPct / 100);
  const rentaAnual = rentaNum * 12;
  const gastosAnual = gastosNum * 12;
  const beneficioAnual = rentaAnual - gastosAnual;
  const rentBruta = precioNum > 0 ? (rentaAnual / precioNum) * 100 : 0;
  const rentNeta = inversionTotal > 0 ? (beneficioAnual / inversionTotal) * 100 : 0;
  const payback = beneficioAnual > 0 ? inversionTotal / beneficioAnual : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Rentabilidad de inversión</CardTitle>
        <CardDescription>
          Calcula la rentabilidad bruta, neta y el payback de comprar un local comercial para alquilar.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="r-precio">Precio del local (€)</Label>
            <Input id="r-precio" type="number" min={0} value={precio} onChange={(e) => setPrecio(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="r-impuestos">Impuestos de compra (%)</Label>
            <Input id="r-impuestos" type="number" min={0} value={impuestosCompra} onChange={(e) => setImpuestosCompra(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="r-renta">Renta mensual esperada (€)</Label>
            <Input id="r-renta" type="number" min={0} value={rentaMensual} onChange={(e) => setRentaMensual(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="r-gastos">Gastos mensuales (IBI, comunidad, seguros… €)</Label>
            <Input id="r-gastos" type="number" min={0} value={gastosMensual} onChange={(e) => setGastosMensual(e.target.value)} />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <ResultCard label="Rentabilidad bruta" value={`${rentBruta.toFixed(2)}%`} />
          <ResultCard label="Rentabilidad neta" value={`${rentNeta.toFixed(2)}%`} />
          <ResultCard label="Recuperación (payback)" value={`${payback.toFixed(1)} años`} />
        </div>
        <p className="text-xs text-muted-foreground">
          La rentabilidad bruta no incluye impuestos ni gastos de compra. La neta sí los considera. El payback es el tiempo estimado para recuperar la inversión total.
        </p>
      </CardContent>
    </Card>
  );
}

/* ─── Valoración de traspaso ─── */
function TraspasoCalc() {
  const [facturacion, setFacturacion] = useState<string>("80000");
  const [beneficio, setBeneficio] = useState<string>("15000");
  const [multiplicador, setMultiplicador] = useState<string>("2.5");
  const [activos, setActivos] = useState<string>("10000");

  const beneficioNum = parseFloat(beneficio) || 0;
  const multNum = parseFloat(multiplicador) || 0;
  const activosNum = parseFloat(activos) || 0;

  const valorTraspaso = beneficioNum * multNum + activosNum;
  const pctSobreFact = parseFloat(facturacion) > 0 ? (valorTraspaso / parseFloat(facturacion)) * 100 : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Valoración de traspaso</CardTitle>
        <CardDescription>
          Estima un rango de precio de traspaso basado en beneficios, activos y multiplicador de sector.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="t-fact">Facturación anual (€)</Label>
            <Input id="t-fact" type="number" min={0} value={facturacion} onChange={(e) => setFacturacion(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="t-ben">Beneficio neto anual (€)</Label>
            <Input id="t-ben" type="number" min={0} value={beneficio} onChange={(e) => setBeneficio(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="t-mult">Multiplicador de sector</Label>
            <Input id="t-mult" type="number" min={0} step={0.1} value={multiplicador} onChange={(e) => setMultiplicador(e.target.value)} />
            <p className="text-xs text-muted-foreground">Restauración ~1.5–2.5 | Retail ~2–3 | Servicios ~2–4</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="t-activos">Valor de activos incluidos (mobiliario, stock… €)</Label>
            <Input id="t-activos" type="number" min={0} value={activos} onChange={(e) => setActivos(e.target.value)} />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <ResultCard label="Valor estimado de traspaso" value={`${valorTraspaso.toLocaleString("es-ES")} €`} />
          <ResultCard label="Multiplicador aplicado" value={`${multNum.toFixed(1)}x`} />
          <ResultCard label="% sobre facturación" value={`${pctSobreFact.toFixed(1)}%`} />
        </div>
        <p className="text-xs text-muted-foreground">
          Es una valoración orientativa. El precio final depende de la ubicación, antigüedad, condiciones del alquiler, cartera de clientes y potencial de crecimiento.
        </p>
      </CardContent>
    </Card>
  );
}

/* ─── Calculadora hipoteca ─── */
function HipotecaCalc() {
  const [capital, setCapital] = useState<string>("120000");
  const [tipo, setTipo] = useState<string>("3.5");
  const [anos, setAnos] = useState<string>("20");

  const c = parseFloat(capital) || 0;
  const r = (parseFloat(tipo) || 0) / 100 / 12;
  const n = (parseFloat(anos) || 0) * 12;

  const cuota = useMemo(() => {
    if (c <= 0 || n <= 0 || r <= 0) return 0;
    return (c * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  }, [c, r, n]);

  const totalPagado = cuota * n;
  const totalIntereses = totalPagado - c;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Simulador de hipoteca</CardTitle>
        <CardDescription>
          Calcula la cuota mensual y el coste total de financiar la compra de un local comercial.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="h-cap">Capital (€)</Label>
            <Input id="h-cap" type="number" min={0} value={capital} onChange={(e) => setCapital(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="h-tipo">Tipo de interés anual (%)</Label>
            <Input id="h-tipo" type="number" min={0} step={0.01} value={tipo} onChange={(e) => setTipo(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="h-anos">Plazo (años)</Label>
            <Input id="h-anos" type="number" min={1} value={anos} onChange={(e) => setAnos(e.target.value)} />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <ResultCard label="Cuota mensual" value={`${cuota.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`} />
          <ResultCard label="Total pagado" value={`${totalPagado.toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`} />
          <ResultCard label="Intereses totales" value={`${totalIntereses.toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`} />
        </div>
        <p className="text-xs text-muted-foreground">
          Cálculo basado en amortización francesa (cuota constante). No incluye gastos de formalización ni seguros.
        </p>
      </CardContent>
    </Card>
  );
}

function ResultCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-muted/30 p-4 text-center">
      <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className="mt-1 text-2xl font-bold text-foreground">{value}</p>
    </div>
  );
}
